import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";
import { toast } from "sonner";

/**
 * VoiceButton — uses browser SpeechRecognition for STT (no backend, no key).
 * Calls onTranscript(text) when user finishes speaking.
 */
export default function VoiceButton({ onTranscript, disabled = false }) {
    const [recording, setRecording] = useState(false);
    const recognitionRef = useRef(null);
    const supportedRef = useRef(true);

    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            supportedRef.current = false;
            return;
        }
        const rec = new SR();
        rec.continuous = false;
        rec.interimResults = false;
        rec.lang = "en-US";

        rec.onresult = (e) => {
            const transcript = Array.from(e.results)
                .map(r => r[0].transcript)
                .join(" ")
                .trim();
            if (transcript) onTranscript?.(transcript);
        };
        rec.onerror = (e) => {
            if (e.error === "not-allowed") toast.error("Mic permission denied");
            else if (e.error !== "no-speech") toast.error(`Voice error: ${e.error}`);
            setRecording(false);
        };
        rec.onend = () => setRecording(false);

        recognitionRef.current = rec;
        return () => {
            try { rec.abort(); } catch {}
        };
    }, [onTranscript]);

    const start = () => {
        if (!supportedRef.current) {
            toast.error("Voice input isn't supported in this browser. Try Chrome.");
            return;
        }
        try {
            recognitionRef.current?.start();
            setRecording(true);
        } catch {
            // already started
        }
    };

    const stop = () => {
        try { recognitionRef.current?.stop(); } catch {}
        setRecording(false);
    };

    return (
        <button
            data-testid="voice-record-btn"
            onClick={recording ? stop : start}
            disabled={disabled}
            className={`relative flex items-center justify-center w-20 h-20 rounded-full transition-transform duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                recording
                    ? "bg-terracotta text-bone ripple"
                    : "bg-clay text-ink hover:bg-clayHover"
            }`}
            aria-label={recording ? "Stop recording" : "Hold to talk"}
        >
            {recording ? <Square className="w-7 h-7" fill="currentColor" /> : <Mic className="w-7 h-7" />}
        </button>
    );
}
